import React, { useContext, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Route } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Container, Link, Paper, TextField, Typography } from '@mui/material';

import config from '../config';
import { MCP_CONNECTIONS_PATH } from '../layout/UserMenu';
import { ContextStore } from '../store/contextStore';
import { leaveDashboardFor, openDashboardAt } from './leaveDashboard';
import {
    OAUTH_HANDOFF_PATH,
    clearStashedRequest,
    describeHandoffFailure,
    franchiseHandoffUrl,
    handoffRedirectTarget,
    isCompanyCode,
    normalizeCompanyCode,
    onCompanyDashboard,
    postHandoff,
    readStashedRequest,
    requestIsSpent,
    stashRequestFromUrl,
    takeSignOutRetry,
} from './oauthHandoff';
import SignIn from './signin';
import { noteSessionRefusal } from './signOutReason';

// /oauth/handoff?req=… — the dashboard's half of the MCP sign-in (backend metaplan A7c, AIE §6).
// Rendered by App before its auth gate, like /reset-password:
//
// 1. `req` moves from the URL into this tab's sessionStorage before anything renders (the
//    tenant's Auth0 round trip drops the query string; its callback URL is this page, which ops
//    add to the tenant's Auth0 application — runbook O-11).
// 2. Without a session it shows the normal sign-in, which comes back here afterwards.
// 3. With one it posts the stashed `req` to POST /v2/edit/oauth/handoff/ once and replaces
//    itself with the redirect_url the backend built (https only), where the authorization
//    server shows its consent screen. A 401 signs the stale session out and comes back here
//    (once per request: the Auth0 sign-in can continue by itself, so a second 401 is reported).
// 4. Franchise path (P7), next to the sign-in: franchise staff type their franchise's company code
//    and continue at its dashboard's /oauth/handoff with the same `req` (FranchiseSignIn).

// Local development may run the authorization server on plain http.
const ALLOW_HTTP = config.env === 'dev';

function Frame({ children }) {
    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', p: 2 }}>
            <Paper sx={{ p: 4, width: '100%', maxWidth: 420 }}>
                <Typography variant="h5" component="h1" gutterBottom>
                    Connect an MCP client
                </Typography>
                {children}
            </Paper>
        </Box>
    );
}

const FRANCHISE_FORM_ID = 'oauth-handoff-franchise';
const CODE_FORMAT = 'Enter a company code: letters, digits and hyphens, not starting or ending with a hyphen.';

// Franchise staff sign in at their franchise's (the parent company's) dashboard, not at this company's: they type
// its code, checked here as a host label only -- never looked up, compared or suggested (nothing on this page may
// tell whether a company exists or is this one's franchise) -- and this tab continues at
// `<code>.<the dashboard domain>/oauth/handoff` with the same `req`, which is not posted here first (it yields
// one code only). There the usual handoff runs with the franchise session and the backend decides. The typed code
// goes nowhere else: not into this page's address (the form submits nothing natively), storage or a request.
function FranchiseSignIn({ pageLocation }) {
    const [open, setOpen] = useState(false);
    const [code, setCode] = useState('');
    const [problem, setProblem] = useState(null);
    const [expired, setExpired] = useState(false);

    const onSubmit = (event) => {
        event.preventDefault();
        const normalized = normalizeCompanyCode(code);
        if (!isCompanyCode(normalized)) {
            setProblem(CODE_FORMAT);
            return;
        }
        const target = franchiseHandoffUrl(normalized, readStashedRequest(), pageLocation);
        setProblem(null);
        if (!target) {
            setExpired(true);
            return;
        }
        openDashboardAt(target);
    };

    return (
        <Box sx={{ mt: 1 }}>
            <Button
                size="small"
                onClick={() => setOpen((was) => !was)}
                aria-expanded={open}
                aria-controls={open ? FRANCHISE_FORM_ID : undefined}
                sx={{ textTransform: 'none', px: 0 }}
            >
                Franchise staff: sign in at your franchise's dashboard
            </Button>
            {open && (
                <Box component="form" id={FRANCHISE_FORM_ID} noValidate onSubmit={onSubmit} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1 }}>
                    <TextField
                        id={`${FRANCHISE_FORM_ID}-code`}
                        label="Your franchise's company code"
                        value={code}
                        onChange={(event) => { setCode(event.target.value); setProblem(null); }}
                        error={!!problem}
                        helperText={problem || "The first part of your franchise's dashboard address."}
                        size="small"
                        fullWidth
                        autoFocus
                        autoComplete="off"
                        inputProps={{ autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }}
                    />
                    <Button type="submit" variant="outlined" sx={{ flexShrink: 0, mt: '1px' }}>Continue</Button>
                </Box>
            )}
            {open && (
                // An unknown code's dashboard never finishes loading (its sign-in setup is refused); this page cannot
                // tell a mistyped code from a real one, so it says so up front.
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                    A mistyped code opens a page that keeps loading. If that happens, go back and check the code.
                </Typography>
            )}
            {open && expired && (
                <Alert severity="error" sx={{ mt: 1 }}>{describeHandoffFailure('missing_request', 0).message}</Alert>
            )}
        </Box>
    );
}

// `pageLocation`: this page's address (tests put the page on a company's dashboard host; jsdom's is localhost).
export default function OAuthHandoffPage({ pageLocation = window.location }) {
    const { token, logout, checkIsAuthenticated, user } = useContext(ContextStore);
    // Before the first render of anything below — SignIn's router reads the URL when it mounts.
    useState(() => stashRequestFromUrl());
    const authenticated = checkIsAuthenticated();

    const [attempt, setAttempt] = useState(0);
    const [phase, setPhase] = useState({ kind: 'working' });
    const sentFor = useRef(-1);
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const hasRequest = !!readStashedRequest();

    useEffect(() => {
        if (!authenticated || sentFor.current === attempt) { return; }
        sentFor.current = attempt;
        const req = readStashedRequest();
        if (!req) {
            setPhase({ kind: 'failed', code: 'missing_request', status: 0 });
            return;
        }
        setPhase({ kind: 'working' });
        postHandoff(token, req).then(
            ({ status, body }) => {
                if (!mounted.current) { return; }
                if (status === 200) {
                    const target = handoffRedirectTarget(body && body.redirect_url, { allowHttp: ALLOW_HTTP });
                    clearStashedRequest();
                    if (!target) {
                        setPhase({ kind: 'failed', code: 'unexpected_redirect', status });
                        return;
                    }
                    setPhase({ kind: 'leaving' });
                    leaveDashboardFor(target);
                    return;
                }
                if (status === 401) {
                    // The session ended on the server: sign out, sign in again, come back here —
                    // once; another 401 after that is reported rather than looped.
                    if (takeSignOutRetry()) {
                        noteSessionRefusal(body);
                        logout(OAUTH_HANDOFF_PATH);
                        return;
                    }
                    setPhase({ kind: 'failed', code: 'session_rejected', status });
                    return;
                }
                if (requestIsSpent(status)) { clearStashedRequest(); }
                const code = body && typeof body.code === 'string' ? body.code : 'unreachable';
                setPhase({ kind: 'failed', code, status });
            },
            () => {
                if (mounted.current) { setPhase({ kind: 'failed', code: 'unreachable', status: 0 }); }
            },
        );
    }, [authenticated, attempt, token, logout]);

    if (!authenticated) {
        if (!hasRequest) {
            return (
                <Frame>
                    <Alert severity="error">{describeHandoffFailure('missing_request', 0).message}</Alert>
                </Frame>
            );
        }
        return (
            <>
                <Container maxWidth="xs" sx={{ pt: 3 }}>
                    <Alert severity="info">Sign in to connect your MCP client to this account.</Alert>
                    {onCompanyDashboard(pageLocation) && <FranchiseSignIn pageLocation={pageLocation} />}
                </Container>
                <BrowserRouter>
                    <Route path="*">
                        <SignIn />
                    </Route>
                </BrowserRouter>
            </>
        );
    }

    const failure = phase.kind === 'failed' ? describeHandoffFailure(phase.code, phase.status) : null;
    return (
        <Frame>
            {user && user.email && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Signed in as {user.email}
                </Typography>
            )}
            {!failure && (
                <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2">
                        {phase.kind === 'leaving' ? 'Continuing to the sign-in server…' : 'Checking your access…'}
                    </Typography>
                </Box>
            )}
            {failure && (
                <>
                    <Alert severity="error" sx={{ mb: 2 }}>{failure.message}</Alert>
                    {failure.retry && (
                        <Button variant="contained" fullWidth sx={{ mb: 2 }} onClick={() => setAttempt((n) => n + 1)}>
                            Try again
                        </Button>
                    )}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {failure.mcpConnections && (
                            <Link href={MCP_CONNECTIONS_PATH} underline="hover" variant="body2">Open MCP connections</Link>
                        )}
                        <Link href="/" underline="hover" variant="body2">Back to the dashboard</Link>
                    </Box>
                </>
            )}
        </Frame>
    );
}
